import api from './api';
import { apiCache, cacheTTL } from '@/utils/api-cache';

import { AdministrationSite } from './travelers';

export interface SelectionDetails {
  selectedBy: { email: string; name: string };
  selectedAt: string;
  pharmacistNote?: string;
  refillInfo?: string;
  administrationSite?: AdministrationSite | null;
  quantity?: string;
}

export interface VaccineRecommendation {
  vaccine_name: string;
  therapy_class: string;
  protocol_id: string;
  status: 'RECOMMENDED' | 'CONDITIONAL' | 'NOT_RECOMMENDED' | 'CONTRAINDICATED';
  reasoning: string;
  dose: string;
  route: string;
  timing: string;
  series_schedule: string;
  counseling_points: string[];
  flags: string[];
  isSelected?: boolean;
  selectionDetails?: SelectionDetails;
}

export interface MedicationRecommendation {
  medication_name: string;
  therapy_class: string;
  protocol_id: string;
  status: 'RECOMMENDED' | 'CONDITIONAL' | 'NOT_RECOMMENDED' | 'CONTRAINDICATED';
  reasoning: string;
  dose: string;
  route: string;
  timing: string;
  frequency: string;
  quantity: number;
  quantity_reasoning: string;
  counseling_points: string[];
  flags: string[];
  isSelected?: boolean;
  selectionDetails?: SelectionDetails;
}

export interface NotRecommendedVaccine {
  vaccine_name: string;
  dose: string;
  route: string;
  status: string;
  timing: string;
  reasoning: string;
  protocol_id: string;
  therapy_class: string;
  flags?: string[];
  itemKey?: string;
  protocolMetadata?: {
    displayName?: string;
    protocolType?: string;
    therapyClass?: string;
    route?: string;
    dosing?: {
      adult?: {
        dose?: string;
        frequency?: string;
        route?: string;
        instructions?: string;
      };
      pediatric?: Array<{
        age_range?: string;
        dose?: string;
        frequency?: string;
        instructions?: string;
      }>;
    };
    timing?: {
      minimum_days_before_travel?: number;
      booster_interval?: string;
    };
    counselingPoints?: string[];
    contraindications?: {
      allergies?: string[];
      conditions?: string[];
    };
    notes?: string;
  };
}

export interface NotRecommendedMedication {
  medication_name: string;
  dose: string;
  route: string;
  status: string;
  quantity: number;
  frequency: string;
  reasoning: string;
  protocol_id: string;
  therapy_class: string;
  flags?: string[];
  itemKey?: string;
  protocolMetadata?: {
    displayName?: string;
    protocolType?: string;
    therapyClass?: string;
    route?: string;
    dosing?: {
      adult?: {
        dose?: string;
        frequency?: string;
        route?: string;
        instructions?: string;
      };
      pediatric?: Array<{
        age_range?: string;
        dose?: string;
        frequency?: string;
        instructions?: string;
      }>;
    };
    timing?: {
      start_days_before?: number;
      days_after_leaving?: number;
    };
    counselingPoints?: string[];
    contraindications?: {
      allergies?: string[];
      conditions?: string[];
    };
    notes?: string;
  };
}

export interface NotRecommended {
  vaccines: NotRecommendedVaccine[];
  medications: NotRecommendedMedication[];
}

export interface AIMetadata {
  provider: string;
  model: string;
  inputTokens: number;
  outputTokens: number;
  latencyMs: number;
}

export interface PharmacistAddedItem {
  protocol_id: string;
  itemKey: string;
  source: string;
  isSelected: boolean;
  name: string;
  therapy_class: string;
  brands?: string[];
  route?: string | null;
  doses?: string | null;
  age_group?: string | null;
  notes?: string | null;
  selectionDetails?: SelectionDetails;
  protocolMetadata?: any;
}

export interface TravelerRecommendation {
  id: string;
  createdAt: string;
  overallReviewLevel: 'LOW' | 'MEDIUM' | 'HIGH';
  validationPassed: boolean;
  vaccines: VaccineRecommendation[];
  medications: MedicationRecommendation[];
  notRecommended?: NotRecommended;
  aiMetadata: AIMetadata;
  pharmacistAddedVaccines?: PharmacistAddedItem[];
  pharmacistAddedMedications?: PharmacistAddedItem[];
  pharmacistNote?: string;
  pharmacistNoteDetails?: {
    addedBy: { email: string; name: string };
    addedAt: string;
  };
}

export interface AIRecommendationsResponse {
  message: string;
  traveler: {
    id: string;
    name: string;
    consultationNumber: string;
  };
  recommendation: TravelerRecommendation;
  disclaimer: string;
}

export const medicationService = {
  /**
   * Get AI-generated recommendations for a traveler
   * Includes vaccine and medication recommendations based on CDC guidelines
   * @param travelerId - The traveler ID
   * @param includeSelectionStatus - Whether to include selection status (isSelected field)
   */
  async getAIRecommendations(
    travelerId: string, 
    includeSelectionStatus: boolean = true
  ): Promise<AIRecommendationsResponse> {
    const cacheKey = `traveler:recommendations:${travelerId}:${includeSelectionStatus}`;
    
    return apiCache.fetch(
      cacheKey,
      async () => {
        const params = new URLSearchParams();
        if (includeSelectionStatus) {
          params.append('include_selection_status', 'true');
        }

        const url = `/pharmacy/travelers/${travelerId}/recommendations${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await api.get<AIRecommendationsResponse>(url);
        return response.data;
      },
      cacheTTL.long // 15 minutes - AI recommendations don't change frequently
    );
  },
};
